import { updateConfigSettings } from '@api/portalServices';
import type { Setting } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import {
  AccountRecoveryEnabledValue,
  AuthnLevel,
  type AuthnStrengthValues,
  FaceAuthVideoStreamValue,
  FieldNames,
  InstanceKey,
  LiveVideoFeedBehavior,
  StoreEnrollmentPhotosValue,
  updateAccountRecoveryEnabledSettingsTemplate,
  updateFaceAuthVideoStreamSettingsTemplate,
  updateSettingsTemplate,
  updateStoreEnrollmentPhotosSettingsTemplate,
  updateUserCanShowLiveFeedSettingsTemplate,
  UserCanShowLiveFeedValue,
} from '@portal/containers/security/constants';
import { useMutation } from '@tanstack/react-query';
import { sleep } from '@utils/utils';

type HubAccessStored = {
  ussSetting: Setting | undefined;
  iamSetting: Setting | undefined;
  accountRecoveryEnabledSetting: Setting | undefined;
};

// `authnStrength` is `null` when the conditions editor owns authentication strength: its fields are
// not rendered in that branch, so there is nothing to diff and nothing to write. A nullable object
// rather than a boolean flag, so the compiler refuses a call that has the settings but not the fields.
type HubAccessValues = {
  authnStrength: AuthnStrengthValues | null;
  accountRecovery: boolean;
};

export type FaceStored = {
  faceAuthVideoStreamSetting: Setting | undefined;
  userCanShowLiveFeedSetting: Setting | undefined;
  storeEnrollmentPhotosSetting: Setting | undefined;
};

// `null` leaves that setting unwritten. An unreadable stored live-feed pair locks its control,
// because any value written would be invented.
type FaceValues = { liveFeed: LiveVideoFeedBehavior | null; storePhotos: boolean | null };

// A shown feed leaves the reveal permission as stored: it has no effect while the feed is shown.
const liveFeedTargets: Record<
  LiveVideoFeedBehavior,
  { feedVisibility: FaceAuthVideoStreamValue; revealPermission?: UserCanShowLiveFeedValue }
> = {
  [LiveVideoFeedBehavior.SHOWN]: { feedVisibility: FaceAuthVideoStreamValue.ON },
  [LiveVideoFeedBehavior.HIDDEN]: {
    feedVisibility: FaceAuthVideoStreamValue.OFF,
    revealPermission: UserCanShowLiveFeedValue.OFF,
  },
  [LiveVideoFeedBehavior.HIDDEN_USER_CAN_SHOW]: {
    feedVisibility: FaceAuthVideoStreamValue.OFF,
    revealPermission: UserCanShowLiveFeedValue.ON,
  },
};

// Values arriving from the configuration service are untrusted strings. Each is
// matched against the two values the service defines and nothing else; anything
// outside that set yields undefined rather than being coerced.
const parseOnOff = (
  setting: Setting | undefined,
  onValue: string,
  offValue: string,
): boolean | undefined => {
  const value = setting?.value?.toLowerCase();
  if (value === onValue) {
    return true;
  }
  if (value === offValue) {
    return false;
  }
  return undefined;
};

/**
 * Resolves the tenant's two stored configuration values into the single behavior
 * the administrator chooses between.
 *
 * Returns undefined when either value is missing or unrecognized. Neither is
 * assigned a meaning: the configuration service guarantees both are present, so
 * an absent value means the response was not what this application depends on,
 * and displaying a behavior the tenant is not subject to is worse than
 * displaying none. The one exception is a shown feed, which resolves on its own
 * because the reveal permission has no effect while the feed is already shown.
 */
export const resolveLiveVideoFeedBehavior = (
  faceAuthVideoStream?: Setting,
  userCanShowLiveFeed?: Setting,
): LiveVideoFeedBehavior | undefined => {
  const feedShown = parseOnOff(
    faceAuthVideoStream,
    FaceAuthVideoStreamValue.ON,
    FaceAuthVideoStreamValue.OFF,
  );

  if (feedShown === undefined) {
    return undefined;
  }
  if (feedShown) {
    return LiveVideoFeedBehavior.SHOWN;
  }

  const userMayShow = parseOnOff(
    userCanShowLiveFeed,
    UserCanShowLiveFeedValue.ON,
    UserCanShowLiveFeedValue.OFF,
  );

  if (userMayShow === undefined) {
    return undefined;
  }
  return userMayShow ? LiveVideoFeedBehavior.HIDDEN_USER_CAN_SHOW : LiveVideoFeedBehavior.HIDDEN;
};

// Diff-only update: a setting is written only when the target differs from what the
// configuration service returned. A setting the service did not return has no value to
// compare against, so the target is written.
const differsFromStored = (stored: Setting | undefined, target: string): boolean =>
  stored?.value?.toLowerCase() !== target;

// Both settings are on for a tenant that has never stored them, so absent reads as on.
export const isStoringEnrollmentPhotos = (stored: Setting | undefined): boolean => {
  const value = stored?.value?.toLowerCase();
  return (
    value === undefined ||
    value === StoreEnrollmentPhotosValue.ON ||
    value === StoreEnrollmentPhotosValue.TRUE
  );
};

export const isAccountRecoveryEnabled = (stored: Setting | undefined): boolean => {
  const value = stored?.value?.toLowerCase();
  return value === undefined || value === AccountRecoveryEnabledValue.ON;
};

export function computeNewSettingValues(values: AuthnStrengthValues): {
  ussNewSettingValue: AuthnLevel;
  iamNewSettingValue: AuthnLevel;
} {
  return {
    ussNewSettingValue: values[FieldNames.USS_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : values[FieldNames.USS_VALUE],
    iamNewSettingValue: values[FieldNames.IAM_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : values[FieldNames.IAM_VALUE],
  };
}

function buildAuthnStrengthSettings(
  { ussSetting, iamSetting }: HubAccessStored,
  authnStrength: AuthnStrengthValues | null,
): Setting[] {
  if (authnStrength === null) {
    return [];
  }
  const { ussNewSettingValue, iamNewSettingValue } = computeNewSettingValues(authnStrength);
  const settings: Setting[] = [];
  if (ussSetting?.value !== ussNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: ussSetting?.settingId,
      instanceKey: InstanceKey.USS,
      value: ussNewSettingValue,
    });
  }
  if (iamSetting?.value !== iamNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: iamSetting?.settingId,
      instanceKey: InstanceKey.IAM,
      value: iamNewSettingValue,
    });
  }
  return settings;
}

export function buildHubAccessSettings(
  stored: HubAccessStored,
  { authnStrength, accountRecovery }: HubAccessValues,
): Setting[] {
  const settings = buildAuthnStrengthSettings(stored, authnStrength);
  const { accountRecoveryEnabledSetting } = stored;
  if (isAccountRecoveryEnabled(accountRecoveryEnabledSetting) !== accountRecovery) {
    // account-recovery-enabled is written without an instanceKey.
    settings.push({
      ...updateAccountRecoveryEnabledSettingsTemplate,
      settingId: accountRecoveryEnabledSetting?.settingId,
      value: accountRecovery ? AccountRecoveryEnabledValue.ON : AccountRecoveryEnabledValue.OFF,
    });
  }
  return settings;
}

const writeFaceSetting = (
  template: Omit<Setting, 'value'>,
  stored: Setting | undefined,
  value: string,
): Setting => ({
  ...template,
  instanceKey: stored?.instanceKey ?? template.instanceKey,
  settingId: stored?.settingId,
  value,
});

export function buildFaceSettings(
  {
    faceAuthVideoStreamSetting,
    userCanShowLiveFeedSetting,
    storeEnrollmentPhotosSetting,
  }: FaceStored,
  { liveFeed, storePhotos }: FaceValues,
): Setting[] {
  const settings: Setting[] = [];

  const storedFeed = resolveLiveVideoFeedBehavior(
    faceAuthVideoStreamSetting,
    userCanShowLiveFeedSetting,
  );
  if (liveFeed !== null && liveFeed !== storedFeed) {
    const { feedVisibility, revealPermission } = liveFeedTargets[liveFeed];
    if (differsFromStored(faceAuthVideoStreamSetting, feedVisibility)) {
      settings.push(
        writeFaceSetting(
          updateFaceAuthVideoStreamSettingsTemplate,
          faceAuthVideoStreamSetting,
          feedVisibility,
        ),
      );
    }
    if (
      revealPermission !== undefined &&
      differsFromStored(userCanShowLiveFeedSetting, revealPermission)
    ) {
      settings.push(
        writeFaceSetting(
          updateUserCanShowLiveFeedSettingsTemplate,
          userCanShowLiveFeedSetting,
          revealPermission,
        ),
      );
    }
  }

  if (
    storePhotos !== null &&
    isStoringEnrollmentPhotos(storeEnrollmentPhotosSetting) !== storePhotos
  ) {
    settings.push(
      writeFaceSetting(
        updateStoreEnrollmentPhotosSettingsTemplate,
        storeEnrollmentPhotosSetting,
        storePhotos ? StoreEnrollmentPhotosValue.ON : StoreEnrollmentPhotosValue.OFF,
      ),
    );
  }

  return settings;
}

export const useUpdateConfigSettings = () => {
  return useMutation<void, ApiError, Setting[]>({
    mutationFn: async settings => {
      if (settings.length > 0) {
        await updateConfigSettings({ settings });
        await sleep(500);
      }
    },
    retry: false,
  });
};
